import React, { useState } from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";

export interface ScreenshotImageProps {
  /** Caminho da imagem */
  src: string;
  /** Texto alternativo */
  alt: string;
  /** Texto do placeholder quando imagem não carrega */
  placeholderText?: string;
  /** Altura mínima do container */
  minHeight?: number | { xs?: number; sm?: number; md?: number; lg?: number };
  /** Estilos adicionais para o container */
  containerSx?: SxProps<Theme>;
  /** Estilos adicionais para a imagem */
  imageSx?: SxProps<Theme>;
  /** Cor de fundo do placeholder */
  backgroundColor?: string;
  /** Border radius do container */
  borderRadius?: number | string;
  /** Box shadow do container */
  boxShadow?: string;
  /** Mostrar overlay gradient */
  showOverlay?: boolean;
  /** Callback quando clicado */
  onClick?: () => void;
  /** Loading lazy ou eager */
  loading?: "lazy" | "eager";
  /** Object fit da imagem */
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
}

const ScreenshotImage: React.FC<ScreenshotImageProps> = ({
  src,
  alt,
  placeholderText,
  minHeight,
  containerSx,
  imageSx,
  backgroundColor = "#1a1a2e",
  borderRadius = 3,
  boxShadow,
  showOverlay = false,
  onClick,
  loading = "lazy",
  objectFit = "contain",
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Normaliza minHeight para objeto
  const normalizedMinHeight =
    typeof minHeight === "number"
      ? { xs: minHeight, md: minHeight }
      : minHeight;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        borderRadius,
        overflow: "hidden",
        backgroundColor,
        cursor: onClick ? "pointer" : "default",
        ...(boxShadow && { boxShadow }),
        ...containerSx,
      }}
    >
      {/* Overlay gradient */}
      {showOverlay && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}

      {/* Imagem real */}
      {!imageError && (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading={loading}
          onError={handleImageError}
          onLoad={handleImageLoad}
          sx={{
            width: "100%",
            height: "auto",
            display: "block",
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 0.3s ease",
            objectFit,
            ...(normalizedMinHeight && { minHeight: normalizedMinHeight }),
            ...imageSx,
          }}
        />
      )}

      {/* Placeholder - mostrado apenas quando a imagem deu erro */}
      {imageError && (
        <Box
          sx={{
            width: "100%",
            minHeight: normalizedMinHeight || 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.5)",
              textAlign: "center",
              px: 2,
            }}
          >
            {placeholderText || alt}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ScreenshotImage;
